import { useEffect } from "react";
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  Input,
  Select,
  Space,
} from "antd";
import {
  BotBillingDetails,
  UpdateBillingDetailsPayload,
  useUpdateBillingDetailsMutation,
} from "api";
import { getErrorMessage } from "components";

interface BotsBillingDetailsFormProps {
  details: BotBillingDetails;
}

const COUNTRY_OPTIONS = [
  { value: "RO", label: "Romania" },
  { value: "BG", label: "Bulgaria" },
  { value: "HU", label: "Ungaria" },
];

export const BotsBillingDetailsForm = ({
  details,
}: BotsBillingDetailsFormProps) => {
  const [form] = Form.useForm<UpdateBillingDetailsPayload>();
  const { notification } = App.useApp();

  const [updateDetails, { isLoading: isSaving }] =
    useUpdateBillingDetailsMutation();

  // Hydrate the form whenever the upstream details change (e.g. after first save).
  useEffect(() => {
    form.setFieldsValue({
      company_name: details.company_name ?? "",
      cui: details.cui ?? "",
      registration_number: details.registration_number ?? "",
      address: details.address ?? "",
      country_code: details.country_code,
      notifications_email: details.notifications_email ?? "",
    });
  }, [details, form]);

  const onSubmit = async (values: UpdateBillingDetailsPayload) => {
    try {
      await updateDetails(values).unwrap();
      notification.success({ message: "Datele de facturare au fost salvate." });
    } catch (e) {
      notification.error({ message: getErrorMessage(e as never) });
    }
  };

  return (
    <Card title="Date facturare (SPOR NETWORK SRL)">
      {!details.has_stripe_customer && (
        <Alert
          type="info"
          showIcon
          message="Completeaza si salveaza datele pentru a inregistra clientul in Stripe."
          description="Datele apar pe facturile emise de ASTECH SOFT catre SPOR NETWORK. Codul de TVA (CUI) este obligatoriu pentru reverse-charge intra-RO."
          style={{ marginBottom: 16 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        requiredMark="optional"
      >
        <Form.Item
          name="company_name"
          label="Denumire firma"
          rules={[{ required: true, min: 2, max: 255 }]}
        >
          <Input maxLength={255} placeholder="SPOR NETWORK SRL" />
        </Form.Item>

        <Space.Compact block>
          <Form.Item
            name="cui"
            label="CUI"
            rules={[{ required: true, min: 2, max: 32 }]}
            style={{ flex: 1, marginRight: 8 }}
            extra="Cu sau fara prefix RO. Sistemul normalizeaza."
          >
            <Input maxLength={32} placeholder="RO12345678" />
          </Form.Item>
          <Form.Item
            name="registration_number"
            label="Nr. Reg. Com."
            rules={[{ required: true, min: 2, max: 64 }]}
            style={{ flex: 1 }}
          >
            <Input maxLength={64} placeholder="J40/123/2024" />
          </Form.Item>
        </Space.Compact>

        <Form.Item
          name="address"
          label="Adresa sediu"
          rules={[{ required: true, min: 5, max: 500 }]}
        >
          <Input.TextArea
            maxLength={500}
            rows={2}
            placeholder="Str. Exemplu Nr. 1, Bucuresti"
          />
        </Form.Item>

        <Form.Item
          name="country_code"
          label="Tara"
          rules={[{ required: true, len: 2 }]}
        >
          <Select options={COUNTRY_OPTIONS} />
        </Form.Item>

        <Form.Item
          name="notifications_email"
          label="Email pentru notificari de facturare"
          rules={[{ required: true, type: "email", max: 255 }]}
          extra="Aici se trimit alertele: plata esuata, grace, dezactivare boti, anulare abonament."
        >
          <Input type="email" maxLength={255} placeholder="billing@spor-network.com" />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0 }}>
          <Button type="primary" htmlType="submit" loading={isSaving}>
            Salveaza
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};
